/**
 * The values the deployed stacks hand the bundle - WEB-004, ADR-031.
 *
 * All four are `CfnOutput`s that `scripts/deploy-web.sh` reads from
 * CloudFormation and exports before `vite build`, so they are baked into the
 * bundle rather than fetched. There is nothing to fetch them *from* before the
 * user is authenticated, and a runtime lookup would be a second failure mode on
 * the first paint.
 *
 * Read through a function rather than at module scope: a missing value has to be
 * a loud failure at the boundary, and a throw during module evaluation takes the
 * whole bundle down with a stack trace nobody can act on.
 */

export interface AppConfig {
  /** `https://api.dev.coworker.kotrov.com` - ADR-031 gave the API its own name. */
  readonly apiUrl: string;
  readonly userPoolId: string;
  readonly userPoolClientId: string;
  /** Origin of the Cognito managed login, e.g. `https://auth.dev.coworker.kotrov.com` (COW-477). */
  readonly loginUrl: string;
}

const KEYS = {
  apiUrl: 'VITE_API_URL',
  userPoolId: 'VITE_USER_POOL_ID',
  userPoolClientId: 'VITE_USER_POOL_CLIENT_ID',
  loginUrl: 'VITE_LOGIN_URL',
} as const;

export class ConfigError extends Error {
  constructor(missing: readonly string[]) {
    super(
      `the build is missing ${missing.join(', ')}. scripts/deploy-web.sh exports these from the ` +
        `auth and api stacks before \`vite build\`; a local run needs them in apps/web/.env.local.`,
    );
    this.name = 'ConfigError';
  }
}

/**
 * @param env normally `import.meta.env`. Taken as an argument so a test can drive
 *   every shape without a bundler, and so the failure is observable.
 */
export function readConfig(env: Record<string, string | undefined>): AppConfig {
  const missing = Object.values(KEYS).filter((key) => (env[key] ?? '').trim() === '');
  if (missing.length > 0) throw new ConfigError(missing);

  return Object.freeze({
    // Trailing slashes make `${apiUrl}/v1/me` a double slash, which API Gateway
    // treats as a different route and answers 404.
    apiUrl: stripTrailingSlash(env[KEYS.apiUrl] as string),
    userPoolId: env[KEYS.userPoolId] as string,
    userPoolClientId: env[KEYS.userPoolClientId] as string,
    loginUrl: stripTrailingSlash(env[KEYS.loginUrl] as string),
  });
}

function stripTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

let cached: AppConfig | undefined;

/** The configuration this bundle was built with. Read once; it cannot change at runtime. */
export function appConfig(): AppConfig {
  cached ??= readConfig(import.meta.env as unknown as Record<string, string | undefined>);
  return cached;
}
